import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import FunctionalQRImg from "../../images/Functional_DelNova_QR.png";


export default function ContactUsRedesign() {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    emailjs
      .sendForm(
        "service_vn3hh7u",
        "template_ohp6ofd",
        form.current,
        "user_97BArShYWL8HlU7aUDVzW"
      )
      .then(
        (result) => {
          console.log(result.text);
          setIsSubmitting(false);
          setSubmitStatus("success");
          form.current.reset();
          // Clear success message after 5 seconds
          setTimeout(() => setSubmitStatus(null), 5000);
        },
        (error) => {
          console.error("EmailJS error:", error);
          setIsSubmitting(false);
          setSubmitStatus("error");
          setErrorMessage(error.text || error.message || JSON.stringify(error) || "Unknown EmailJS error");
        }
      );
  };

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* 1. Hero Header Section */}
      <section className="relative min-h-[35vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1423662055902-3f9e42ee1a63?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Get in Touch
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Connect & Innovate
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-slate-300 font-light leading-relaxed">
            Partner with DelNova Healthcare. Reach out to our Thane R&D facility for formulation development, analytical services, and business collaborations.
          </p>
        </div>
      </section>

      {/* 2. Main Contact Grid */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Corporate Information & Office Details */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full">
                Corporate Office
              </div>
              <h2 className="text-3xl font-bold text-brand-blue tracking-tight">
                DelNova Healthcare
              </h2>
              <p className="text-slate-500 font-light leading-relaxed text-sm md:text-base">
                An Innovation Center of <span className="font-semibold text-brand-blue">ViRACS Healthcare</span>. We provide client-satisfactory pharmaceutical solutions with Quality in Design built into every molecule.
              </p>
            </div>

            {/* Contacts details list */}
            <div className="space-y-6 bg-white border border-slate-100 rounded-2xl p-6 md:p-8 shadow-sm">
              {/* Address */}
              <div className="flex items-start space-x-4 border-b border-slate-100 pb-5">
                <div className="w-10 h-10 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal mt-1 flex-shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="space-y-2.5">
                  <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">Facility Address</h4>
                  <p className="text-slate-600 text-sm font-light leading-relaxed">
                    A220, Road No. 16V, Behind Ashar IT Park, Wagle Industrial Estate, Thane (West) - 400 604 (India)
                  </p>
                  <a
                    href="https://goo.gl/maps/t6nxpLJjoduVoaam8"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs font-semibold text-brand-teal hover:text-brand-teal-dark transition-colors group"
                  >
                    <span>Get Directions on Google Maps</span>
                    <svg className="w-3.5 h-3.5 ml-1 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Call Details */}
              <div className="flex items-start space-x-4 border-b border-slate-100 pb-5">
                <div className="w-10 h-10 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal mt-1 flex-shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="space-y-2.5">
                  <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">Phone Contacts</h4>
                  <p className="text-slate-600 text-sm font-light">
                    Office Mobile: +91 89997 16061<br />
                    Direct Contact: +91 98698 50770
                  </p>
                  <a
                    href="tel:9869850770"
                    className="inline-flex items-center text-xs font-semibold text-brand-teal hover:text-brand-teal-dark transition-colors group"
                  >
                    <span>Click to Call</span>
                    <svg className="w-3.5 h-3.5 ml-1 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Email & Web */}
              <div className="flex items-start space-x-4 border-b border-slate-100 pb-5">
                <div className="w-10 h-10 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal mt-1 flex-shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="space-y-2.5">
                  <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">Online Channels</h4>
                  <p className="text-slate-600 text-sm font-light">
                    Email: info@delnovahealth.com<br />
                    Website: www.delnovahealth.com
                  </p>
                  <div className="flex space-x-4">
                    <a
                      href="mailto:info@delnovahealth.com"
                      className="inline-flex items-center text-xs font-semibold text-brand-teal hover:text-brand-teal-dark transition-colors"
                    >
                      Email Us
                    </a>
                    <a
                      href="https://delnovahealth.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center text-xs font-semibold text-brand-teal hover:text-brand-teal-dark transition-colors"
                    >
                      Visit Website
                    </a>
                  </div>
                </div>
              </div>

              {/* WhatsApp Option */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center text-green-500 mt-1 flex-shrink-0">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div className="space-y-2.5">
                  <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">Instant Chat</h4>
                  <p className="text-slate-600 text-sm font-light">
                    Message us directly on WhatsApp for real-time support.
                  </p>
                  <a
                    href="https://wa.me/919869850770"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs font-semibold text-green-600 hover:text-green-700 transition-colors group"
                  >
                    <span>Chat on WhatsApp</span>
                    <svg className="w-3.5 h-3.5 ml-1 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Access QR Code Card */}
            <div className="bg-white border border-slate-100 rounded-2xl p-6 md:p-8 shadow-sm flex flex-col sm:flex-row items-center gap-6 hover:shadow-md transition-shadow duration-300">
              <div className="w-28 h-28 bg-slate-50 border border-slate-100 rounded-xl p-2.5 flex items-center justify-center flex-shrink-0 hover:scale-[1.02] transition-transform duration-300">
                <img 
                  src={FunctionalQRImg} 
                  alt="DelNova Research and Development QR Code" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="space-y-2 text-center sm:text-left">
                <span className="inline-block text-[9px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2 py-0.5 rounded-full">
                  Quick Access
                </span>
                <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">R&D Page Portal</h4>
                <p className="text-slate-500 font-light text-xs leading-relaxed">
                  Scan this high-quality QR code with your smartphone camera to quickly jump directly to our Research & Development page.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Dynamic Form & Branding Photo */}
          <div className="lg:col-span-7 space-y-8">
            {/* Interactive Form Card */}
            <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brand-blue to-brand-teal" />
              
              <div className="mb-6 space-y-1 text-left">
                <h3 className="text-2xl font-bold text-brand-blue tracking-tight">Drop Us A Line!</h3>
                <p className="text-slate-500 font-light text-sm">
                  Fill out the form below, and our business development or scientific desk will reply within 24 hours.
                </p>
              </div>

              {/* Success / Error State banners */}
              {submitStatus === "success" && (
                <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm flex items-start space-x-2.5 animate-fadeIn">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <span className="font-semibold block">Thank you!</span>
                    Your message was sent successfully. We will get back to you shortly.
                  </div>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start space-x-2.5 animate-fadeIn">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <span className="font-semibold block">Submission Failed</span>
                    There was a problem sending your message: <span className="font-medium">{errorMessage}</span>. Please try again or email us directly.
                  </div>
                </div>
              )}

              {/* Form elements */}
              <form ref={form} onSubmit={sendEmail} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5 text-left">
                    <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Name</label>
                    <input 
                      type="text" 
                      name="from_name"
                      required
                      placeholder="Your Full Name"
                      className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                    />
                  </div>
                  <div className="space-y-1.5 text-left">
                    <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Organization</label>
                    <input 
                      type="text" 
                      name="user_organization"
                      placeholder="Company or Institution"
                      className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5 text-left">
                    <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Email Address</label>
                    <input 
                      type="email" 
                      name="user_email"
                      required
                      placeholder="name@company.com"
                      className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                    />
                  </div>
                  <div className="space-y-1.5 text-left">
                    <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Contact Number</label>
                    <input 
                      type="tel" 
                      name="user_Mo_Number"
                      pattern="[0-9]{10}"
                      maxLength="10"
                      placeholder="10-Digit Mobile Number"
                      className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1.5 text-left">
                  <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Message</label>
                  <textarea 
                    name="message"
                    required
                    rows="4"
                    placeholder="How can we help your generic or innovative formulation project?"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-brand-blue hover:bg-brand-blue/90 text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2 ${
                    isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Sending Message...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Message</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contextual Branding Image removed */}

          </div>

        </div>
      </section>
    </div>
  );
}
